import React, { useEffect, useState } from 'react';
import { Building2, Edit, Plus, RefreshCw, Trash2, Users, Briefcase, GraduationCap, UserCog, FileText } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import { ALL_FILIALES_ID } from '../data/filiales';

const Administration = () => {
  const { user } = useAuth();
  const { filialeId } = useFiliale();
  const [activeTab, setActiveTab] = useState('departements');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const tabs = [
    { id: 'departements', label: 'Départements', icon: Building2 },
    { id: 'titres', label: 'Titres/Postes', icon: Briefcase },
    { id: 'types-contrat', label: 'Types de contrat', icon: FileText },
    { id: 'types-personnel', label: 'Types de personnel', icon: UserCog },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = `/api/${activeTab}`;
      
      // Filtrer par filiale pour les tables spécifiques par filiale (titre, type_personnel)
      const tablesWithFiliale = ['titres', 'types-personnel'];
      if (tablesWithFiliale.includes(activeTab) && filialeId && filialeId !== 'all' && filialeId !== ALL_FILIALES_ID) {
        endpoint += `?filiale_id=${filialeId}`;
      }
      
      const response = await http.get(endpoint);
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab, filialeId]);

  const handleCreate = () => {
    setEditingItem(null);
    // departement, type_contrat sont globaux (pas de filiale_id)
    // titre, type_personnel nécessitent une filiale
    const tablesWithFiliale = ['titres', 'types-personnel'];
    if (tablesWithFiliale.includes(activeTab)) {
      if (filialeId === 'all' || filialeId === ALL_FILIALES_ID) {
        alert(`Veuillez sélectionner une filiale spécifique dans le Périmètre avant de créer un ${activeTab === 'titres' ? 'titre' : 'type de personnel'}.`);
        return;
      }
      setFormData({ filiale_id: parseInt(filialeId) });
    } else {
      // Tables globales : pas de filiale_id
      setFormData({});
    }
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) return;

    try {
      await http.delete(`/api/${activeTab}/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/${activeTab}/${editingItem.id || editingItem.iddepartement || editingItem.idtitre || editingItem.idtype_contrat || editingItem.idtype_personnel}`, formData);
      } else {
        await http.post(`/api/${activeTab}`, formData);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const renderForm = () => {
    const commonFields = (
      <div className="space-y-4">
        {/* Pas de sélection de filiale dans le formulaire - utilise la filiale du header */}
      </div>
    );

    switch (activeTab) {
      case 'departements':
        return (
          <div className="space-y-4">
            {commonFields}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-night-blue">Libellé</label>
              <input
                type="text"
                className="input-field"
                value={formData.libelle_departement || ''}
                onChange={(e) => setFormData({ ...formData, libelle_departement: e.target.value })}
                required
              />
            </div>
          </div>
        );
      case 'titres':
        return (
          <div className="space-y-4">
            {commonFields}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-night-blue">Nom développé</label>
              <input
                type="text"
                className="input-field"
                value={formData.nom_developpe || ''}
                onChange={(e) => setFormData({ ...formData, nom_developpe: e.target.value })}
                required
              />
            </div>
          </div>
        );
      case 'types-contrat':
        return (
          <div className="space-y-4">
            {commonFields}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-night-blue">Nom</label>
              <input
                type="text"
                className="input-field"
                value={formData.nom || ''}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                required
              />
            </div>
          </div>
        );
      case 'types-personnel':
        return (
          <div className="space-y-4">
            {commonFields}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-night-blue">Libellé</label>
              <input
                type="text"
                className="input-field"
                value={formData.libelle || ''}
                onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                required
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderTable = () => {
    const columns = {
      departements: ['iddepartement', 'libelle_departement', 'nombre_personnel', 'filiale_code'],
      titres: ['idtitre', 'nom_developpe', 'nombre_personnel', 'filiale_code'],
      'types-contrat': ['idtype_contrat', 'nom', 'nombre_personnel', 'filiale_code'],
      'types-personnel': ['idtype_personnel', 'libelle', 'nombre_personnel', 'filiale_code'],
    };

    const labels = {
      departements: { libelle_departement: 'Département' },
      titres: { nom_developpe: 'Titre' },
      'types-contrat': { nom: 'Type de contrat' },
      'types-personnel': { libelle: 'Type de personnel' },
    };

    const currentColumns = columns[activeTab];
    const currentLabels = labels[activeTab];

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {currentLabels[Object.keys(currentLabels)[0]]}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Personnel
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Filiale
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((item, index) => {
              const itemId = item.id || item.iddepartement || item.idtitre || item.idtype_contrat || item.idtype_personnel;
              return (
                <tr key={itemId || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {itemId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                    {item[Object.keys(currentLabels)[0]]}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900 px-2 py-0.5 text-xs font-medium text-blue-600 dark:text-blue-300">
                      {item.nombre_personnel || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {item.filiale_code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(itemId)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const ActiveIcon = tabs.find((t) => t.id === activeTab)?.icon || Building2;

  return (
    <div>
      <BlueHeader
        title="Administration"
        subtitle="Gestion des données de référence"
        icon={ActiveIcon}
        showBack={false}
      />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-[#0F1E3D]">Administration</h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#E3A81C] text-[#0F1E3D] rounded-lg hover:bg-[#EDB52E] transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Nouveau
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-blue-200 bg-white rounded-lg p-2 shadow-sm">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            Chargement…
          </div>
        ) : data.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            Aucune donnée pour cette section.
          </div>
        ) : (
          renderTable()
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-blue-100">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4">
              <h3 className="text-lg font-bold text-blue-900">
                {editingItem ? 'Modifier' : 'Créer'} {tabs.find((t) => t.id === activeTab)?.label}
              </h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-900"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {renderForm()}
              <div className="mt-6 flex justify-end gap-3 border-t border-blue-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editingItem ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Administration;
